<script setup lang="ts">
import { computed } from 'vue'

import type { SheetSharePicker } from '../composables/useDataVisibleExportActions'
import type {
  LoadReportFilterDropdownValues,
  ReportFilterDropdownables,
} from '../composables/useReportFilterDropdownables'
import { REPORT_TOOLBAR_ICONS } from '../configs/reportToolbarIcons'
import type {
  ChartFieldRoleSelection,
  ChartFieldRoleSources,
  ChartType,
  ChartTypeMeta,
  ReportViewType,
} from '../models/chart.types'
import type { ReportColumn } from '../models/column.types'
import type { ReportFilter, SavedReportFilterOption } from '../models/filter.types'
import type { ResolvedReportTableConfig } from '../models/tableConfig.types'
import type { ReportCardStatus, ReportTableType } from '../models/tablePresentation.types'
import type { TrendTableFrequency } from '../models/trendTable.types'
import { toReportColumnOption } from '../service/reportColumnOption'

import ChartFieldRoles from './chart/ChartFieldRoles.vue'
import ChartTypePicker from './chart/ChartTypePicker.vue'
import DataVisibleActions from './controls/DataVisibleActions.vue'
import ReportTableModeControls from './controls/ReportTableModeControls.vue'
import ReportToolbarSelect from './controls/ReportToolbarSelect.vue'
import SheetShareModal from './controls/SheetShareModal.vue'
import ViewTypeToggle from './controls/ViewTypeToggle.vue'
import ReportFilters from './filters/ReportFilters.vue'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, type SelectOption, type SortState } from '@/shared/ui'
import type { UiAsyncState } from '@/shared/ui/types'

const props = withDefaults(
  defineProps<{
    view: ReportViewType
    chartType: ChartType
    views: readonly ReportViewType[]
    chartTypes: readonly ChartTypeMeta[]
    chartRoles?: ChartTypeMeta['fieldRoles']
    chartFieldSources?: ChartFieldRoleSources
    chartFieldSelection?: ChartFieldRoleSelection
    chartFieldsDisabled?: boolean
    columns: readonly ReportColumn[]
    tableModes?: boolean
    tableTypes: readonly ReportTableType[]
    tableType: ReportTableType
    breakdown: string | null
    frequency: TrendTableFrequency
    savedViewId: string | null
    savedViews: readonly { id: string; name: string }[]
    filters: ReportFilter[]
    savedFilters: readonly SavedReportFilterOption[]
    dropdownables?: ReportFilterDropdownables
    onLoadDropdownValues?: LoadReportFilterDropdownValues
    readOnly?: boolean
    state: UiAsyncState
    tableConfig: ResolvedReportTableConfig
    sort?: SortState
    exportDisabled?: boolean
    exportBusy?: boolean
    /** The Sheets export "share with" step (ORP-25238); hosted here beside the export action. */
    sharePicker?: SheetSharePicker
    fullscreen?: boolean
    allowHideChart?: boolean
    allowShowChart?: boolean
    recipe?: 'single' | 'chart-table-chart' | 'chart-table-table'
    maintenanceScheduleId?: number | null | undefined
    cardStatus?: ReportCardStatus
    customizeOpen?: boolean
  }>(),
  {
    chartRoles: () => [],
    chartFieldSources: () => ({ dimensions: [], metrics: [] }),
    chartFieldSelection: () => ({}),
    chartFieldsDisabled: false,
    dropdownables: () => ({}),
    readOnly: false,
    exportDisabled: false,
    exportBusy: false,
    fullscreen: false,
    allowHideChart: false,
    allowShowChart: false,
    recipe: 'single',
    sort: null,
    cardStatus: 'all',
    customizeOpen: false,
    tableModes: true,
  },
)

const emit = defineEmits<{
  view: [value: ReportViewType]
  chartType: [value: ChartType]
  chartFields: [value: ChartFieldRoleSelection]
  tableType: [value: ReportTableType]
  breakdown: [value: string | null]
  frequency: [value: TrendTableFrequency]
  sort: [value: SortState]
  savedView: [value: string | null]
  filterDraft: [value: ReportFilter[]]
  applyFilters: [value: ReportFilter[]]
  saveFilter: [value: { id?: string; name: string; filter: ReportFilter }]
  removeFilter: [id: string]
  customize: []
  exportSheet: []
  downloadExcel: []
  toggleFullscreen: []
  hideChart: []
  showChart: []
  addToDashboard: [view: ReportViewType]
  cardStatus: [value: ReportCardStatus]
  renameSavedView: [value: { id: string; name: string }]
  removeSavedView: [id: string]
}>()
defineSlots<{
  controls?: (props: { state: UiAsyncState; tableConfig: ResolvedReportTableConfig }) => unknown
}>()

const { t } = useI18n()
const cardSortOptions = computed<SelectOption<string>[]>(() =>
  props.columns
    .filter((column) => column.numeric && !column.invisible)
    .map((column) => toReportColumnOption(column)),
)
// Filters offer every selectable field, not just the columns currently shown in the table.
const filterColumns = computed(() =>
  props.columns.map((column) => (column.invisible ? { ...column, invisible: false } : column)),
)
const isChartTableChart = computed(() => props.recipe === 'chart-table-chart')
const showFilters = computed(
  () => !props.readOnly && ['table', 'chart', 'card'].includes(props.view),
)

function updateCardSort(field: string | number | null): void {
  if (field === null) return
  emit('sort', { field: String(field), dir: 'desc' })
}
</script>

<template>
  <div
    class="data-visible-toolbar flex items-center flex-nowrap gap-[5px] overflow-x-auto py-0 max-md:flex-wrap max-md:overflow-x-visible"
    :class="[`data-visible-toolbar--${view}`, `data-visible-toolbar--${recipe}`]"
  >
    <div
      v-if="showFilters"
      class="data-visible-toolbar__filters flex-auto min-w-0 max-md:basis-[100%] max-md:ml-0"
    >
      <ReportFilters
        :compact-height="view === 'card'"
        :model-value="filters"
        :columns="filterColumns"
        :saved-filters="savedFilters"
        :dropdownables="dropdownables"
        :on-load-dropdown-values="onLoadDropdownValues"
        @update:model-value="emit('filterDraft', $event)"
        @apply="emit('applyFilters', $event)"
        @save="emit('saveFilter', $event)"
        @remove="emit('removeFilter', $event)"
      />
    </div>

    <div
      v-if="$slots.controls"
      class="data-visible-toolbar__custom flex w-max items-center gap-[5px] min-w-0"
    >
      <slot name="controls" :state="state" :table-config="tableConfig" />
    </div>

    <div
      class="data-visible-toolbar__primary flex items-center flex-nowrap gap-[5px] ml-auto max-md:basis-[100%] max-md:ml-0 max-md:flex-wrap"
    >
      <ReportTableModeControls
        v-if="view === 'table' && !readOnly && tableModes"
        class="data-visible-toolbar__modes"
        :table-type="tableType"
        :breakdown="breakdown"
        :frequency="frequency"
        :saved-view-id="savedViewId"
        :columns="columns"
        :table-types="tableTypes"
        :saved-views="savedViews"
        compact
        @update:table-type="emit('tableType', $event)"
        @update:breakdown="emit('breakdown', $event)"
        @update:frequency="emit('frequency', $event)"
        @update:saved-view-id="emit('savedView', $event)"
        @rename-saved-view="emit('renameSavedView', $event)"
        @remove-saved-view="emit('removeSavedView', $event)"
      />
      <ReportToolbarSelect
        v-if="view === 'card' && cardSortOptions.length"
        class="data-visible-toolbar__card-sort"
        data-testid="report-card-sort"
        :model-value="sort?.field ?? null"
        :options="cardSortOptions"
        :label="t('reports.card.sortBy')"
        :icon="REPORT_TOOLBAR_ICONS.sort"
        :placeholder="t('reports.card.sortByPlaceholder')"
        searchable
        @update:model-value="updateCardSort"
      />
      <ChartTypePicker
        v-if="view === 'chart' && chartTypes.length > 1 && state === 'ready'"
        :model-value="chartType"
        :types="chartTypes"
        compact
        @update:model-value="emit('chartType', $event)"
      />
      <ChartFieldRoles
        v-if="view === 'chart' && chartRoles.length > 0 && state === 'ready'"
        class="data-visible-toolbar__chart-fields overflow-visible"
        :model-value="chartFieldSelection"
        :roles="chartRoles"
        :sources="chartFieldSources"
        :disabled="chartFieldsDisabled"
        :clear-label="t('ui.clear')"
        compact
        @update:model-value="emit('chartFields', $event)"
      />
      <Button
        v-if="(view === 'table' || view === 'card') && !readOnly"
        class="data-visible-toolbar__action w-max text-primary-muted"
        :class="{ 'bg-surface-sunken': customizeOpen }"
        size="lg"
        variant="ghost"
        square
        :aria-label="t('reports.table.customize')"
        :aria-pressed="customizeOpen"
        :title="t('reports.table.customize')"
        @click="emit('customize')"
      >
        <Icon :name="REPORT_TOOLBAR_ICONS.customize" :size="18" aria-hidden="true" />
      </Button>
      <ViewTypeToggle
        v-if="views.length > 1"
        :model-value="view"
        :views="views"
        :chart-disabled="chartTypes.length === 0"
        @update:model-value="emit('view', $event)"
      />
      <Button
        v-if="!isChartTableChart"
        class="data-visible-toolbar__action w-max text-primary-muted"
        size="lg"
        variant="ghost"
        square
        :aria-label="
          fullscreen ? t('reports.actions.exitFullscreen') : t('reports.actions.fullscreen')
        "
        :title="fullscreen ? t('reports.actions.exitFullscreen') : t('reports.actions.fullscreen')"
        @click="emit('toggleFullscreen')"
      >
        <Icon
          :name="
            fullscreen ? REPORT_TOOLBAR_ICONS.exitFullscreen : REPORT_TOOLBAR_ICONS.enterFullscreen
          "
          :size="18"
          aria-hidden="true"
        />
      </Button>
      <DataVisibleActions
        v-if="view !== 'cohort' && (!isChartTableChart || allowHideChart || allowShowChart)"
        :menu-label="t('reports.actions.more')"
        :export-sheet-label="t('reports.actions.exportSheet')"
        :download-excel-label="t('reports.actions.downloadExcel')"
        :fullscreen-label="t('reports.actions.fullscreen')"
        :exit-fullscreen-label="t('reports.actions.exitFullscreen')"
        :hide-chart-label="t('reports.actions.hideChart')"
        :show-chart-label="t('reports.actions.showChart')"
        :disabled="exportDisabled"
        :allow-export="!readOnly && !isChartTableChart"
        :allow-add-to-dashboard="!readOnly && !isChartTableChart"
        :allow-fullscreen="false"
        :allow-hide-chart="allowHideChart"
        :allow-show-chart="allowShowChart"
        :add-to-dashboard-label="t('reports.actions.addToDashboard')"
        :export-sheet-busy="exportBusy"
        :download-excel-busy="exportBusy"
        :fullscreen="fullscreen"
        :maintenance-schedule-id="maintenanceScheduleId"
        :clear-cache-label="t('reports.maintenance.clearCache')"
        :execute-analysis-label="t('reports.maintenance.executeAnalysis')"
        @export-sheet="emit('exportSheet')"
        @download-excel="emit('downloadExcel')"
        @toggle-fullscreen="emit('toggleFullscreen')"
        @hide-chart="emit('hideChart')"
        @show-chart="emit('showChart')"
        @add-to-dashboard="emit('addToDashboard', view)"
      />
      <SheetShareModal v-if="sharePicker" :picker="sharePicker" />
    </div>
  </div>
</template>

<style scoped>
.data-visible-toolbar
  .data-visible-toolbar__primary
  > :deep(.ui-select:not(.report-toolbar-select)) {
  min-width: 9rem;
}
.data-visible-toolbar.data-visible-toolbar--card {
  gap: 5px;
}
.data-visible-toolbar.data-visible-toolbar--card .data-visible-toolbar__primary {
  gap: 5px;
}
.data-visible-toolbar.data-visible-toolbar--card .data-visible-toolbar__filters {
  flex: 1 1 18rem;
}
.data-visible-toolbar.data-visible-toolbar--table .data-visible-toolbar__filters {
  order: 0;
}
.data-visible-toolbar.data-visible-toolbar--table .data-visible-toolbar__custom {
  order: 1;
}
.data-visible-toolbar.data-visible-toolbar--table .data-visible-toolbar__primary {
  order: 2;
}
.data-visible-toolbar.data-visible-toolbar--card
  .data-visible-toolbar__filters
  :deep([data-pdf-filter-root]) {
  box-sizing: border-box;
  height: 2.5rem;
  min-height: 2.5rem;
}
.data-visible-toolbar.data-visible-toolbar--card :deep(.ui-select__control) {
  box-sizing: border-box;
  height: 2rem;
  min-height: 2rem;
}

.data-visible-toolbar__filters :deep([data-pdf-filter-root]) {
  min-width: 0;
}
.data-visible-toolbar__filters :deep([data-pdf-filter-input-wrap]) {
  min-width: 0;
  width: auto;
}
.data-visible-toolbar__filters :deep([data-pdf-filter-input]) {
  min-width: 0;
  width: 100%;
}
</style>
